/**
 * Google Analytics 4 ecommerce event tracking (client-side)
 */

import type { EcommerceItem, SubscriptionDetails } from './types'
import { createEcommerceItem } from './utils'

/**
 * Track view_pricing event
 * Fired when user views the pricing page
 */
export function trackViewPricing(): void {
  if (typeof window === 'undefined' || !window.gtag) {
    return
  }

  try {
    window.gtag('event', 'view_pricing', {
      page_location: window.location.href,
    })
    console.log('GA4 event: view_pricing')
  } catch (error) {
    console.error('Failed to track view_pricing:', error)
  }
}

/**
 * Track view_item_list event
 * Fired when user sees the list of subscription plans
 */
export function trackViewItemList(items: EcommerceItem[]): void {
  if (typeof window === 'undefined' || !window.gtag) {
    return
  }

  try {
    window.gtag('event', 'view_item_list', {
      item_list_id: 'subscription_plans',
      item_list_name: 'Subscription Plans',
      items,
    })
    console.log('GA4 event: view_item_list', items.length, 'items')
  } catch (error) {
    console.error('Failed to track view_item_list:', error)
  }
}

/**
 * Track select_item event
 * Fired when user selects a specific plan configuration
 */
export function trackSelectItem(item: EcommerceItem): void {
  if (typeof window === 'undefined' || !window.gtag) {
    return
  }

  try {
    window.gtag('event', 'select_item', {
      item_list_id: 'subscription_plans',
      item_list_name: 'Subscription Plans',
      items: [item],
    })
    console.log('GA4 event: select_item', item.item_id)
  } catch (error) {
    console.error('Failed to track select_item:', error)
  }
}

/**
 * Track begin_checkout event
 * Fired when user clicks the subscribe button
 */
export function trackBeginCheckout(item: EcommerceItem, value: number): void {
  if (typeof window === 'undefined' || !window.gtag) {
    return
  }

  try {
    window.gtag('event', 'begin_checkout', {
      currency: 'USD',
      value,
      items: [item],
    })
    console.log('GA4 event: begin_checkout', {
      item_id: item.item_id,
      value,
    })
  } catch (error) {
    console.error('Failed to track begin_checkout:', error)
  }
}

/**
 * Track add_payment_info event
 * Fired when user reaches Stripe checkout page
 */
export function trackAddPaymentInfo(item: EcommerceItem, value: number): void {
  if (typeof window === 'undefined' || !window.gtag) {
    return
  }

  try {
    window.gtag('event', 'add_payment_info', {
      currency: 'USD',
      value,
      payment_type: 'stripe',
      items: [item],
    })
    console.log('GA4 event: add_payment_info')
  } catch (error) {
    console.error('Failed to track add_payment_info:', error)
  }
}

/**
 * Track purchase event (client-side)
 * This is a backup/confirmation event. Primary purchase tracking happens server-side.
 */
export function trackPurchase(
  transactionId: string,
  value: number,
  items: EcommerceItem[],
  coupon?: string,
): void {
  if (typeof window === 'undefined' || !window.gtag) {
    return
  }

  try {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value,
      currency: 'USD',
      items,
      ...(coupon ? { coupon } : {}),
    })
    console.log('GA4 event: purchase (client-side)', {
      transaction_id: transactionId,
      value,
    })
  } catch (error) {
    console.error('Failed to track purchase:', error)
  }
}

/**
 * Convenience function to track subscription checkout flow
 */
export function trackSubscriptionCheckout(details: SubscriptionDetails): void {
  const item = createEcommerceItem(details)
  trackBeginCheckout(item, details.amount)
}
